import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Badge } from "./ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

// Sep 2026 — Buyer-SKU BOM change history modal, reachable from
// BomLookup's row Actions column. Reads via
// `GET /api/bom/buyer-sku/{buyer_sku_id}/history`, which merges the
// buyer SKU's own `brand_specific_bom` changes with the parent
// `bidso_sku_id`'s `common_bom` changes. AC6 empty state included.
export default function BomHistoryDialog({ open, onClose, buyerSkuId }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !buyerSkuId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(
          `${API}/api/bom/buyer-sku/${encodeURIComponent(buyerSkuId)}/history`,
        );
        if (!cancelled) setRows(data?.history || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, buyerSkuId]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-3xl"
        data-testid="bom-history-dialog"
      >
        <DialogHeader>
          <DialogTitle>BOM Change History — {buyerSkuId}</DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
        {!loading && error && (
          <div className="text-sm text-red-600" data-testid="bom-history-error">
            {String(error)}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div
            className="text-sm text-zinc-500 py-8 text-center"
            data-testid="bom-history-empty"
          >
            No BOM changes recorded yet for this SKU.
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm" data-testid="bom-history-table">
              <thead className="sticky top-0 bg-zinc-50">
                <tr className="text-left">
                  <th className="p-2">Date</th>
                  <th className="p-2">Layer</th>
                  <th className="p-2">What Changed</th>
                  <th className="p-2">Changed By</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="p-2 whitespace-nowrap text-zinc-600">
                      {r.timestamp
                        ? new Date(r.timestamp).toLocaleString()
                        : "—"}
                    </td>
                    <td className="p-2">
                      <Badge
                        variant="outline"
                        className={
                          r.layer === "brand"
                            ? "border-violet-300 text-violet-700 bg-violet-50"
                            : "border-sky-300 text-sky-700 bg-sky-50"
                        }
                      >
                        {r.layer === "brand" ? "Brand" : "Common"}
                      </Badge>
                    </td>
                    <td className="p-2">
                      <div className="font-medium">{r.action}</div>
                      <div className="text-zinc-500 text-xs">{r.summary || "—"}</div>
                    </td>
                    <td className="p-2 text-zinc-600">{r.actor || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
